import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Package, Zap, Target, Users, Video, QrCode, Star, Gift, RefreshCw, Shield, BarChart3, UserPlus, TrendingUp, Clock } from 'lucide-react';
import { VenueSelection } from '../../types/map';

interface CatalogItem {
  id: string;
  name: string;
  description: string;
  setupPrice: number;
  recurringPrice: number;
  unit: string;
  category: 'placement' | 'awareness' | 'engagement';
  icon: React.ReactNode;
  image?: string;
  unitType: string;
  chartData?: {
    metrics: Array<{
      label: string;
      value: number | string;
      unit: string;
      color: string;
      icon: React.ReactNode;
    }>;
    timeframe: string;
  };
}

type BlankableNumber = number | '';

interface RetailMediaStepProps {
  venueSelections: VenueSelection[];
  skuSelections: { machineId: string; row: number; col: number }[];
  retailMediaSelections: Record<string, any>;
  onRetailMediaSelectionsChange: (selections: Record<string, any>) => void;
  venues?: Array<{ venueName: string; venueType: string }>; // Optional venues array to get venue types
}

// Removed hardcoded locationPricingTiers - now using API-fetched prices with multipliers

// Helper function to get human-friendly name from item ID
const getRetailMediaDisplayName = (itemId: string): string => {
  const item = catalogItems.find(i => i.id === itemId);
  return item?.name || itemId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const catalogItems: CatalogItem[] = [
  {
    id: 'dooh-ad',
    name: 'Digital Screen Ad',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Target className="w-6 h-6" />,
    image: '/calculator-images/2.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Brand Awareness Lift', value: 22, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> },
        { label: 'Total Impressions', value: '~37,500', unit: '/month/machine', color: 'bg-pink-500', icon: <TrendingUp className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'home-banner',
    name: 'Home Screen Banner',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/3.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 4, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Brand Awareness Uplift', value: 12, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'category-icon-lead',
    name: 'Category Icon Lead',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'awareness',
    icon: <Zap className="w-6 h-6" />,
    image: '/calculator-images/4.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 4, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 12, unit: '%', color: 'bg-orange-500', icon: <BarChart3 className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'category-banner',
    name: 'Category Banner',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Target className="w-6 h-6" />,
    image: '/calculator-images/5.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 5, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share Increase', value: 8, unit: '%', color: 'bg-blue-500', icon: <BarChart3 className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'premium-listing',
    name: 'Premium Product Placement',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/6.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 12, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 18, unit: '%', color: 'bg-blue-500', icon: <Package className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'exclusive-category',
    name: 'Exclusive Category',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Category Per Machine',
    category: 'placement',
    icon: <Shield className="w-6 h-6" />,
    image: '/calculator-images/7.png',
    unitType: 'Categories',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 12, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Market Share', value: 6, unit: '%', color: 'bg-blue-500', icon: <BarChart3 className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 15, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'enhanced-detail',
    name: 'Enhanced Product Page',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'engagement',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/8.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 1.5, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Conversion Rate', value: 10, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'vend-video',
    name: 'Vend Video Ad',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Video className="w-6 h-6" />,
    image: '/calculator-images/9.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Captive Audience', value: 100, unit: '%', color: 'bg-green-500', icon: <Video className="w-4 h-4" /> },
        { label: 'Brand Awareness Lift', value: 6, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'virtual-sku',
    name: 'Virtual SKU Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Package className="w-6 h-6" />,
    image: '/calculator-images/10.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Conversion Rate', value: '~8', unit: '%', color: 'bg-green-500', icon: <Package className="w-4 h-4" /> },
        { label: 'Retention Rate', value: 80, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'bundle-sku',
    name: 'Bundle SKU Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Gift className="w-6 h-6" />,
    image: '/calculator-images/11.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Opt In Rate', value: 8, unit: '%', color: 'bg-green-500', icon: <Package className="w-4 h-4" /> },
        { label: 'Intent to Purchase', value: 80, unit: '%', color: 'bg-blue-500', icon: <Gift className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'subscription-sku',
    name: 'Subscription SKU Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <RefreshCw className="w-6 h-6" />,
    image: '/calculator-images/12.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Subscription Conversion', value: 4, unit: '%', color: 'bg-green-500', icon: <RefreshCw className="w-4 h-4" /> },
        { label: 'Customer Retention', value: 80, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'safe-qr',
    name: 'SafeQR Redirect',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per QR Per Machine',
    category: 'engagement',
    icon: <QrCode className="w-6 h-6" />,
    image: '/calculator-images/13.png',
    unitType: 'QR Codes',
    chartData: {
      metrics: [],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'customer-survey',
    name: 'Customer Survey',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Survey Per Machine',
    category: 'engagement',
    icon: <BarChart3 className="w-6 h-6" />,
    image: '/calculator-images/14.png',
    unitType: 'Surveys',
    chartData: {
      metrics: [
        { label: 'Survey Responses', value: 850, unit: '+', color: 'bg-green-500', icon: <BarChart3 className="w-4 h-4" /> },
        { label: 'Completion Rate', value: 52, unit: '%', color: 'bg-blue-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'customer-signup',
    name: 'Customer Sign-Up',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'engagement',
    icon: <UserPlus className="w-6 h-6" />,
    image: '/calculator-images/15.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Opt-In Rate', value: 20, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> },
        { label: 'Email Marketing ROI', value: 38, unit: '%', color: 'bg-purple-500', icon: <TrendingUp className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  }
];

export function RetailMediaStep({
  venueSelections,
  skuSelections,
  retailMediaSelections,
  onRetailMediaSelectionsChange,
  venues = [],
}: RetailMediaStepProps) {
  // Initialize selectedItems from props, excluding the _timePeriod key
  const getInitialItems = () => {
    const items: { [key: string]: { machines: BlankableNumber; units: BlankableNumber } } = {};
    Object.entries(retailMediaSelections).forEach(([key, value]) => {
      if (key !== '_timePeriod' && value && typeof value === 'object' && 'machines' in value && 'units' in value) {
        items[key] = value as { machines: BlankableNumber; units: BlankableNumber };
      }
    });
    return items;
  };

  const [selectedItems, setSelectedItems] = useState<{ [key: string]: { machines: BlankableNumber; units: BlankableNumber } }>(getInitialItems);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<4 | 8 | 12>(
    (retailMediaSelections._timePeriod as 4 | 8 | 12) || 8
  );
  const [totalSetupPrice, setTotalSetupPrice] = useState(0);
  const [totalRecurringPrice, setTotalRecurringPrice] = useState(0);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [selectedProductDetail, setSelectedProductDetail] = useState<string | null>(null);
  const [itemPrices, setItemPrices] = useState<Record<string, { setupPriceCents: number; recurringPriceCents: number }>>({});
  const [loadingPrices, setLoadingPrices] = useState(true);
  
  // Use refs to track previous values and prevent unnecessary syncs
  const prevSelectedItemsRef = useRef(selectedItems);
  const prevTimePeriodRef = useRef(selectedTimePeriod);
  const isInitialMountRef = useRef(true);
  const callbackRef = useRef(onRetailMediaSelectionsChange);
  
  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = onRetailMediaSelectionsChange;
  }, [onRetailMediaSelectionsChange]);

  // Calculate total machines and SKUs from previous steps
  const totalMachines = venueSelections.reduce((sum, vs) => sum + vs.machineCount, 0);
  
  // Calculate unique SKU positions (row, col combinations) for display/pre-population
  const uniqueSKUPositions = useMemo(() => {
    const uniquePositions = new Set(
      skuSelections.map(s => `${s.row}-${s.col}`)
    );
    return uniquePositions.size;
  }, [skuSelections]);
  
  // Keep total SKUs for any calculations that need the full count
  const totalSKUs = skuSelections.length;

  // Extract unique venue types from selected venues
  const selectedVenueTypes = useMemo(() => {
    const venueTypesSet = new Set<string>();
    venueSelections.forEach(vs => {
      const venue = venues.find(v => v.venueName === vs.venueName);
      if (venue && venue.venueType) {
        venueTypesSet.add(venue.venueType);
      }
    });
    return Array.from(venueTypesSet);
  }, [venueSelections, venues]);

  // Fetch prices with multipliers applied
  // Prices persist once fetched - only fetch when we have venue types, never clear unless starting fresh
  useEffect(() => {
    const fetchPrices = async () => {
      // If we have venue selections but venues haven't loaded yet, wait for them
      if (venueSelections.length > 0 && venues.length === 0) {
        setLoadingPrices(true); // Keep loading state while waiting
        return; // Don't clear prices, just wait for venues to load
      }
      
      // Only fetch if we have venue types - don't clear prices if we don't
      // Prices should persist once fetched
      if (selectedVenueTypes.length === 0) {
        setLoadingPrices(false);
        return; // Keep existing prices, don't fetch or clear
      }

      try {
        setLoadingPrices(true);
        const res = await fetch('/api/store/retail-media-pricing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ venueTypes: selectedVenueTypes }),
        });

        if (res.ok) {
          const prices = await res.json();
          const priceMap: Record<string, { setupPriceCents: number; recurringPriceCents: number }> = {};
          prices.forEach((price: any) => {
            priceMap[price.itemId] = {
              setupPriceCents: price.setupPriceCents,
              recurringPriceCents: price.recurringPriceCents,
            };
          });
          setItemPrices(priceMap);
        } else {
          console.error('Failed to fetch retail media pricing');
          // Don't clear prices on error - keep what we have
        }
      } catch (error) {
        console.error('Error fetching retail media pricing:', error);
        // Don't clear prices on error - keep what we have
      } finally {
        setLoadingPrices(false);
      }
    };

    fetchPrices();
  }, [selectedVenueTypes, venueSelections, venues]);

  // Sync selectedItems from props when retailMediaSelections changes externally (e.g., from cart)
  const prevRetailMediaSelectionsRef = useRef(retailMediaSelections);
  useEffect(() => {
    // Skip if this is the same reference (no external change)
    if (prevRetailMediaSelectionsRef.current === retailMediaSelections) {
      return;
    }
    
    const itemsFromProps: { [key: string]: { machines: BlankableNumber; units: BlankableNumber } } = {};
    Object.entries(retailMediaSelections).forEach(([key, value]) => {
      if (key !== '_timePeriod' && value && typeof value === 'object' && 'machines' in value && 'units' in value) {
        itemsFromProps[key] = value as { machines: BlankableNumber; units: BlankableNumber };
      }
    });
    
    // Only update if props actually changed and we have items
    const prevItems = prevRetailMediaSelectionsRef.current;
    const prevItemsFromProps: { [key: string]: { machines: BlankableNumber; units: BlankableNumber } } = {};
    Object.entries(prevItems).forEach(([key, value]) => {
      if (key !== '_timePeriod' && value && typeof value === 'object' && 'machines' in value && 'units' in value) {
        prevItemsFromProps[key] = value as { machines: BlankableNumber; units: BlankableNumber };
      }
    });
    
    const propsChanged = JSON.stringify(itemsFromProps) !== JSON.stringify(prevItemsFromProps);
    if (propsChanged && Object.keys(itemsFromProps).length > 0) {
      setSelectedItems(itemsFromProps);
    }
    
    prevRetailMediaSelectionsRef.current = retailMediaSelections;
  }, [retailMediaSelections]);

  // Initialize pre-selections when first entering step 3 (only if no selections exist)
  useEffect(() => {
    // Only initialize if selectedItems is empty and we have machines selected
    if (Object.keys(selectedItems).length === 0 && totalMachines > 0) {
      const skuUnits = uniqueSKUPositions > 0 ? uniqueSKUPositions : 1;
      const initialSelections: { [key: string]: { machines: BlankableNumber; units: BlankableNumber } } = {
        'dooh-ad': { machines: totalMachines, units: 1 },
        'home-banner': { machines: totalMachines, units: 1 },
        'category-icon-lead': { machines: totalMachines, units: skuUnits },
        'category-banner': { machines: totalMachines, units: 1 },
        'premium-listing': { machines: totalMachines, units: skuUnits },
        'enhanced-detail': { machines: totalMachines, units: skuUnits },
      };
      setSelectedItems(initialSelections);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueSelections, totalMachines, uniqueSKUPositions]); // Include uniqueSKUPositions to update when SKU selections change

  // Sync selectedItems and time period to parent component
  useEffect(() => {
    // Skip on initial mount to prevent unnecessary sync
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevSelectedItemsRef.current = selectedItems;
      prevTimePeriodRef.current = selectedTimePeriod;
      return;
    }

    // Only sync if values actually changed
    const itemsChanged = JSON.stringify(prevSelectedItemsRef.current) !== JSON.stringify(selectedItems);
    const timePeriodChanged = prevTimePeriodRef.current !== selectedTimePeriod;

    if (itemsChanged || timePeriodChanged) {
      prevSelectedItemsRef.current = selectedItems;
      prevTimePeriodRef.current = selectedTimePeriod;
      
      // Use ref to avoid dependency on callback function
      callbackRef.current({
        ...selectedItems,
        _timePeriod: selectedTimePeriod, // Store time period with selections
      });
    }
  }, [selectedItems, selectedTimePeriod]); // Removed onRetailMediaSelectionsChange from dependencies

  // Calculate pricing using API-fetched prices
  useEffect(() => {
    if (loadingPrices || Object.keys(itemPrices).length === 0) {
      setTotalSetupPrice(0);
      setTotalRecurringPrice(0);
      return;
    }

    let setupTotal = 0;
    let recurringTotal = 0;

    Object.entries(selectedItems).forEach(([itemId, config]) => {
      if (config.machines === '' || config.units === '') return;
      const machines = typeof config.machines === 'number' ? config.machines : 0;
      const units = typeof config.units === 'number' ? config.units : 0;

      if (machines > 0 && units > 0) {
        const price = itemPrices[itemId];
        if (!price) return;

        // Setup price is per item (not multiplied by machines/units)
        // Recurring price is per machine per unit, multiplied by time period
        const setupPrice = price.setupPriceCents;
        const recurringPrice = price.recurringPriceCents * machines * units * (selectedTimePeriod / 4);
        
        setupTotal += setupPrice;
        recurringTotal += recurringPrice;
      }
    });

    setTotalSetupPrice(setupTotal);
    setTotalRecurringPrice(recurringTotal);
  }, [selectedItems, selectedTimePeriod, itemPrices, loadingPrices]);

  const handleMachineChange = (itemId: string, machines: BlankableNumber) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        machines: machines === '' ? '' : machines,
        units: prev[itemId]?.units || 0,
      },
    }));
  };

  const handleUnitChange = (itemId: string, units: BlankableNumber) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        machines: prev[itemId]?.machines || 0,
        units: units === '' ? '' : units,
      },
    }));
  };


  const getTotalQuantity = (itemId: string) => {
    const config = selectedItems[itemId];
    if (!config) return 0;
    const machines = config.machines === '' ? 0 : config.machines;
    const units = config.units === '' ? 0 : config.units;
    return machines * units;
  };

  const isSelected = (itemId: string) => getTotalQuantity(itemId) > 0;

  const getCurrentPricing = (item: CatalogItem) => {
    const price = itemPrices[item.id];
    if (!price) {
      return { setupPrice: 0, recurringPrice: 0 };
    }
    // Setup price stays the same regardless of time period
    // Recurring price is multiplied by time period multiplier (1x for 4 weeks, 2x for 8 weeks, 3x for 12 weeks)
    const timePeriodMultiplier = selectedTimePeriod / 4;
    return {
      setupPrice: price.setupPriceCents,
      recurringPrice: price.recurringPriceCents * timePeriodMultiplier,
    };
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Retail Media Calculator</h2>
        <p className="text-slate-600">
          Select additional retail media listings.
        </p>
      </div>

      {/* Pricing Summary Header */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 px-4 py-3">
        <div className="flex gap-2 mb-2">
          {[4, 8, 12].map((weeks) => (
            <button
              key={weeks}
              onClick={() => setSelectedTimePeriod(weeks as 4 | 8 | 12)}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-colors ${
                selectedTimePeriod === weeks
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50'
              }`}
            >
              {weeks} Weeks
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex flex-col">
            <span className="font-bold text-blue-900">Setup:</span>
            <span className="text-xs text-blue-700">one time per setup</span>
          </div>
          <span className="font-bold text-blue-900">£{(totalSetupPrice / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="font-bold text-blue-900 mr-2">{selectedTimePeriod} Week Cost:</span>
          <span className="font-bold text-blue-900">£{(totalRecurringPrice / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div className="flex items-center justify-between text-sm pt-2 border-t border-blue-300 mt-1">
          <span className="font-bold text-blue-900">Total:</span>
          <span className="font-bold text-blue-900">£{((totalSetupPrice + totalRecurringPrice) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
      </div>

      {/* Items Grid - Exact same as PricingCalculator "all items" view */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {catalogItems.map((item) => (
            <div
              key={item.id}
              className={`rounded-xl shadow-sm border transition-all duration-300 overflow-hidden cursor-pointer flex ${
                isSelected(item.id)
                  ? 'bg-blue-200 border-blue-400 shadow-lg text-gray-900'
                  : 'bg-white border-gray-200 hover:shadow-lg hover:border-blue-300'
              }`}
              onClick={() => {
                if (!isSelected(item.id)) {
                  setSelectedItems((prev) => ({
                    ...prev,
                    [item.id]: { machines: 1, units: 1 },
                  }));
                } else {
                  setSelectedItems((prev) => ({
                    ...prev,
                    [item.id]: { machines: 0, units: 0 },
                  }));
                }
              }}
            >
              {/* Image Container - Left Side */}
              {item.image && (
                <div
                  className="w-32 bg-white overflow-hidden cursor-pointer flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProductDetail(item.id);
                  }}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Content Container - Right Side */}
              <div className="p-6 flex-1 flex flex-col justify-between">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-2 rounded-lg ${
                        item.category === 'placement'
                          ? 'bg-green-100 text-green-600'
                          : item.category === 'awareness'
                          ? 'bg-blue-100 text-blue-600'
                          : item.category === 'engagement'
                          ? 'bg-purple-100 text-purple-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {item.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProductDetail(item.id);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    View Listing
                  </button>
                </div>

                <div className="flex items-end justify-between">
                  <div className="text-sm">
                    <div className="font-bold text-gray-900">
                      Setup: £{(getCurrentPricing(item).setupPrice / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      <span className="text-xs font-normal text-gray-500 ml-1">(one time per setup)</span>
                    </div>
                    <div className="font-bold text-gray-900">{selectedTimePeriod} Weeks: £{(getCurrentPricing(item).recurringPrice / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div className={`text-xs mt-1 ${isSelected(item.id) ? 'text-gray-600' : 'text-gray-500'}`}>{item.unit}</div>
                  </div>
                  <div className="flex flex-col space-y-2">
                      {/* Machines Control */}
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs w-16 ${isSelected(item.id) ? 'text-gray-600' : 'text-gray-500'}`}>Machines:</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMachineChange(item.id, (selectedItems[item.id]?.machines || 0) - 1);
                          }}
                          className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                          disabled={!selectedItems[item.id]?.machines}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={selectedItems[item.id]?.machines === 0 || selectedItems[item.id]?.machines === '' ? '' : selectedItems[item.id]?.machines || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                            const finalValue = value === 0 ? '' : value;
                            handleMachineChange(item.id, finalValue);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="0"
                          className="w-12 h-6 text-center text-sm font-medium border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMachineChange(item.id, (selectedItems[item.id]?.machines || 0) + 1);
                          }}
                          className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                        >
                          +
                        </button>
                      </div>

                      {/* Units Control */}
                      <div className="flex items-center space-x-2">
                        <span className="text-xs w-16 text-gray-500">{item.unitType}:</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnitChange(item.id, (selectedItems[item.id]?.units || 0) - 1);
                          }}
                          className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                          disabled={!selectedItems[item.id]?.units}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={selectedItems[item.id]?.units === 0 || selectedItems[item.id]?.units === '' ? '' : selectedItems[item.id]?.units || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                            const finalValue = value === 0 ? '' : value;
                            handleUnitChange(item.id, finalValue);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="0"
                          className="w-12 h-6 text-center text-sm font-medium border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnitChange(item.id, (selectedItems[item.id]?.units || 0) + 1);
                          }}
                          className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                        >
                          +
                        </button>
                      </div>
                    </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedProductDetail && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedProductDetail(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const item = catalogItems.find((i) => i.id === selectedProductDetail);
              if (!item) return null;

              return (
                <div>
                  {/* Header */}
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`p-2 rounded-lg ${
                          item.category === 'placement'
                            ? 'bg-green-100 text-green-600'
                            : item.category === 'awareness'
                            ? 'bg-blue-100 text-blue-600'
                            : item.category === 'engagement'
                            ? 'bg-purple-100 text-purple-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {item.icon}
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">{item.name}</h2>
                    </div>
                    <button
                      onClick={() => setSelectedProductDetail(null)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Image */}
                    {item.image && (
                      <div className="mb-6">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full max-w-md mx-auto rounded-lg shadow-sm border border-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Case Study Charts */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Key Benefits</h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{item.chartData?.timeframe}</span>
                        </div>
                      </div>

                      {item.chartData && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {item.chartData.metrics.map((metric, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <div className={`p-2 rounded-lg ${metric.color} text-white`}>{metric.icon}</div>
                                  <span className="text-sm font-medium text-gray-700">{metric.label}</span>
                                </div>
                              </div>

                              {/* Progress Bar */}
                              <div className="mb-2">
                                <div className="flex items-end space-x-2 mb-1">
                                  <span className="text-2xl font-bold text-gray-900">
                                    {typeof metric.value === 'number' && metric.value > 0 && !metric.unit.includes('+') ? '+' : ''}
                                    {metric.value}
                                  </span>
                                  <span className="text-lg font-medium text-gray-600 mb-1">{metric.unit}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${metric.color} transition-all duration-1000 ease-out`}
                                    style={{
                                      width: `${Math.min(Math.abs(typeof metric.value === 'number' ? metric.value : 100), 100)}%`,
                                      animationDelay: `${index * 200}ms`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Pricing Summary */}
                    <div className="mb-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <h3 className="text-lg font-semibold text-blue-900 mb-3">Pricing</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-900">£{(getCurrentPricing(item).setupPrice / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div className="text-sm text-blue-700">Setup Cost</div>
                          <div className="text-xs text-blue-600 mt-1">one time per setup</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-900">£{(getCurrentPricing(item).recurringPrice / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div className="text-sm text-blue-700">{selectedTimePeriod} Week Cost</div>
                        </div>
                      </div>
                      <div className="text-center mt-3 pt-3 border-t border-blue-200">
                        <span className="text-xs text-blue-600">{item.unit}</span>
                      </div>
                    </div>

                    {/* Special handling for Premium Product Listing */}
                    {item.id === 'premium-listing' && (
                      <div className="mb-6">
                        <div className="space-y-4">
                          <p className="text-gray-600">
                            Premium product listing ensures your products get maximum visibility and higher conversion rates.
                          </p>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-blue-800 text-sm">
                              <strong>Note:</strong> Premium product listing guarantees you 1 of the top 6 slots on the machine, read left to
                              right, top to bottom, first come first serve.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}


                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => {
                          setSelectedProductDetail(null);
                          if (!isSelected(item.id)) {
                            setSelectedItems((prev) => ({
                              ...prev,
                              [item.id]: { machines: 1, units: 1 },
                            }));
                          } else {
                            setSelectedItems((prev) => ({
                              ...prev,
                              [item.id]: { machines: 0, units: 0 },
                            }));
                          }
                        }}
                        className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
                          isSelected(item.id)
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        {isSelected(item.id) ? 'Already Added' : 'Add to Calculator'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
